import type { SelectionModel } from '@gto/ui/data-table'
import { isRowSelected } from '@gto/ui/data-table'

/**
 * The selected rows, resolved against the rows IN HAND.
 *
 * A DS-behaviour workaround, not feature logic, which is why it lives beside the table rather
 * than in each page: DataGridPro pruned a selected id the moment its row left the loaded set,
 * the DS keeps whatever it was handed. Every write built from the raw model therefore risks
 * naming a record that has since paged out or been refetched away — on the settlement builder
 * that meant POSTing a payout the server no longer considers eligible.
 *
 * `isRowSelected` is the DS's own predicate, so BOTH selection modes resolve here without the
 * caller knowing which one it is in — including `allMatching`, where every row in hand counts
 * except the explicitly deselected ones. That is what makes the settlement builder's
 * `selectAllScope: 'allMatching'` exact: its eligible set is bounded and fully loaded, so
 * "all matching" and "all rows passed here" are the same set.
 *
 * A table whose matching set is NOT fully loaded (server-paged, infinite) must not read a count
 * from this — it would report only the rows in hand. That case wants the DS's own
 * `deriveBulkSelection`, which carries the server total.
 */
export const selectedRows = <R>(model: SelectionModel, rows: R[], getId: (row: R) => string): R[] =>
  rows.filter((row) => isRowSelected(model, getId(row)))
